'use client';

import { PortalModal, usePortalContext } from '@cloai/uikit';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { FC, useRef } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { PORTALS } from '@/.constants/portals';
import { Header } from '@/src/components/Header';
import {
  useStyleContext,
  useGTMContext,
  useAutoMaskContext,
  useProfileContext,
  useMobileContext,
} from '@/src/providers';
import { getSource } from '@/src/utils/getSource';

import { CloseAppButton } from '../_generate_image/CloseAppButton';
import { UploadButton } from '../_generate_image/UploadButton';

const styles = {
  portal: '!h-screen !max-h-screen !rounded-none !bg-none !bg-fill-menu !p-0',
  head: 'absolute top-0 left-0 right-0 z-20 py-2 px-5 flex justify-center',
  wrapper: 'shrink-0 bg-fill-menu',
  imageWrap:
    'relative shrink-0 flex flex-col items-center pb-9 mb-5 justify-end w-full aspect-square',
  image:
    'absolute w-full h-full top-0 left-0 object-cover object-top pointer-events-none',
  gradient: 'absolute top-0 left-0 right-0 bottom-0 bg-gradient-xl',
  name: 'relative text-2xl font-bold mb-3',
  innerWrap: 'relative p-5',
  info: 'flex flex-col gap-4 p-4 mb-4 bg-fill-quinary rounded-xl',
  infoTitle: 'text-sm font-bold mb-3',
  infoList: 'list-disc pl-4',
  infoListItem: 'text-sm font-medium text-label-secondary',
  carouselContainer: 'relative overflow-hidden w-full aspect-square',
  carousel: 'flex duration-700 cursor-pointer',
};

export const GenerateInpaintPortal: FC = () => {
  const t = useTranslations('imageModal');
  const { genderMode, prevPortal } = useStyleContext();
  const profile = useProfileContext()!;
  const { isMobiles } = useMobileContext();

  const sendGTM = useGTMContext();
  const { closePortal, openPortal } = usePortalContext();
  const { setInpaintFile } = useAutoMaskContext();

  const toastSuccessPhotoAddedIdRef = useRef<string | number | null>(null);

  const handleUploadButtonClick = () => {
    sendGTM({
      event: 'upload_photo',
      type: null,
      block: 'Inpaint',
      category_name: null,
      name: null,
      category_position: null,
      vitrina_position: null,
      gender: genderMode,
      source: getSource(prevPortal),
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const dataUrl = reader.result as string;

      setInpaintFile((prev) => ({
        ...prev,
        id: 'inpaint-mode',
        fileUrl: dataUrl,
        file,
      }));

      sendGTM({
        event: 'upload_photo_complete',
        type: null,
        block: 'Inpaint',
        category_name: null,
        name: null,
        category_position: null,
        vitrina_position: null,
        gender: genderMode,
        source: getSource(prevPortal),
      });

      closePortal();
      openPortal(PORTALS.InpaintMode);

      toastSuccessPhotoAddedIdRef.current = toast(t('successPhotoAdded'), {
        autoClose: 3000,
        position: 'bottom-center',
        icon: (
          <Image
            width={16}
            height={18}
            src="/icons/check.svg"
            alt="check"
            className="w-4 h-4"
          />
        ),
        theme: 'dark',
        style: {
          backgroundColor: '#3D3E41',
          color: '#FFFFFF',
          fontSize: '14px',
          fontWeight: '500',
          borderRadius: '12px',
          padding: '16px',
          bottom: '32px',
          maxWidth: 'calc(100% - 40px)',
          alignItems: 'start',
        },
      });
    };

    reader.readAsDataURL(file);
  };

  const handleClosePortal = () => {
    if (prevPortal === 'allTools') {
      closePortal();
      openPortal(PORTALS.AllTools);
    } else {
      closePortal();
    }
  };

  return (
    <PortalModal
      portalId={PORTALS.GenerateInpaint}
      className={styles.portal}
      noClose
    >
      <div className="h-full relative">
        <div className={twMerge(styles.head, isMobiles && 'pt-[100px]')}>
          <Header isCloseButton onClickCloseButton={handleClosePortal} />
        </div>
        <div
          className={twMerge(
            'h-full overflow-y-auto',
            isMobiles && 'pt-[100px]',
          )}
        >
          <div className={styles.wrapper}>
            <div className={styles.carouselContainer}>
              <div className={styles.carousel}>
                <div className={styles.imageWrap}>
                  {/* <Image
                fill
                src="/images/inpaint-modal-new.webp"
                alt="inpaint"
                className={styles.image}
                quality={95}
              /> */}
                  <video
                    autoPlay
                    loop
                    muted
                    className={styles.image}
                    playsInline
                    webkit-playsinline="true"
                  >
                    <source src="/images/inpaint.mp4" />
                  </video>
                  <div className={styles.gradient} />
                  {/* <div className={styles.name}>{m('title')}</div> */}
                </div>
              </div>
            </div>

            <div className={styles.innerWrap}>
              <div className={styles.info}>
                <div>
                  <div className={styles.infoTitle}>
                    {t('info.howItWorks.title')}
                  </div>
                  <ul className={styles.infoList}>
                    {t.rich('info.howItWorks.inpaintText', {
                      li: (chunks: React.ReactNode) => (
                        <li className={styles.infoListItem}>{chunks}</li>
                      ),
                    })}
                  </ul>
                </div>
                <div>
                  <div className={styles.infoTitle}>{t('info.title')}</div>
                  <ul className={styles.infoList}>
                    <li className={styles.infoListItem}>
                      {t('info.photoByPhotoText')}
                    </li>
                  </ul>
                </div>
              </div>

              {profile?.isAcitveStartFunnel ? (
                <CloseAppButton
                  text={t('buttonPay')}
                  keybord="paymentDownSale4"
                />
              ) : (
                <UploadButton
                  disabled={false}
                  isMultiple={false}
                  buttonText={t('buttonUploadPhoto')}
                  onChange={handleFileChange}
                  onClick={handleUploadButtonClick}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </PortalModal>
  );
};
